import React, { memo, useCallback, useMemo, useState } from 'react';

import { Modal, SliderCheckbox, SpinnerOverlay } from '@components/common';
import MultiSearch from '@components/common/Table/MultiSearch';
import Table from '@components/common/Table/Table';
import TextAvatar from '@components/common/TextAvatar/TextAvatar';
import {
	TOnApprove,
} from '@components/routes/AdminPanel/AssetsRent/RequestReservationCard/RequestActionConfirmationModal/RequestActionConfirmationModal';

import { useMutateAcceptAssetByUnit } from '@hooks/assets/useMutateAcceptAssetByUnit';
import { useMutateDeclineAssetByUnit } from '@hooks/assets/useMutateDeclineAssetByUnit';
import { useMutateRevokeAssetByUnit } from '@hooks/assets/useMutateRevokeAssetByUnit';
import { useQueryApprovalInfo } from '@hooks/assets/useQueryApprovalInfo';
import { useQueryUser } from '@hooks/useQueryUser';

import { formatDate } from '@utils/date';

import { ISortBy, ITableSearch, ITableSort } from '@interfaces/components/table';
import { IAssetRentRequestCardItem } from '@interfaces/rent/assetRent';

import { EUserRoles } from '@enums/common';

import { approveBtnTitle, confirmTitle, disapproveBtnTitle, revokeBtnTitle } from './constants';

import RequestActionConfirmationModal from '../RequestActionConfirmationModal';
import ApprovalUnitsInfoRow from './ApprovalUnitsInfoRow';
import { getColumns } from './manageApprovalUnitsModalColumns';
import NotesModal from './NotesModal';

import styles from './ApprovalUnitsModal.module.scss';

interface IApprovalUnitsModalProps {
	isAO: boolean;
	asset: IAssetRentRequestCardItem;
	reFetchRequestCardHandler: () => void;
	reFetchItems: () => void;
	isRequestItemsLoading: boolean;
	requestStatus: string;
}

const ApprovalUnitsModal = ({
	isAO,
	asset,
	reFetchRequestCardHandler,
	reFetchItems,
	isRequestItemsLoading,
	requestStatus,
}: IApprovalUnitsModalProps) => {
	const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
	const [notesForModal, setNotesForModal] = useState<string | null>();

	const openNotesModal = (notes: string) => {
		setNotesForModal(notes);

		setIsNotesModalOpen(true);
	};

	const closeNotesModal = () => {
		setNotesForModal(null);

		setIsNotesModalOpen(false);
	};

	const { user } = useQueryUser();

	const [isApprovalModalOpen, setIsApprovalModalOpen] = useState<boolean>(false);
	const [isDeclineModalOpen, setIsDeclineModalOpen] = useState<boolean>(false);
	const [isRevokeModalOpen, setIsRevokeModalOpen] = useState<boolean>(false);
	const [approvalUnitId, setApprovalUnitId] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState<string>('');
	const [isShowOnlyMyApprovals, setIsShowOnlyMyApprovals] = useState<boolean>(false);
	const [sortDirection, setSortDirection] = useState<boolean>(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);

	const handleSort = (sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
	};

	const sort: ITableSort = {
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort: handleSort,
	};

	const {
		approvalInfoItems,
		refetchApprovalInfo,
		isLoadingApprovalInfo,
	} = useQueryApprovalInfo({
		assetId: asset.id,
		isAO,
		searchValue,
	});

	const { revokeAssetByApprovalUnit } = useMutateRevokeAssetByUnit({
		assetId: asset.id,
		isAO,
		onSuccessCallbacks: [refetchApprovalInfo, reFetchRequestCardHandler, reFetchItems],
	});

	const { acceptAssetByApprovalUnit } = useMutateAcceptAssetByUnit({
		assetId: asset.id,
		isAO,
		onSuccessCallbacks: [refetchApprovalInfo, reFetchRequestCardHandler, reFetchItems],
	});

	const { declineAssetByApprovalUnit } = useMutateDeclineAssetByUnit({
		assetId: asset.id,
		isAO,
		onSuccessCallbacks: [refetchApprovalInfo, reFetchRequestCardHandler, reFetchItems],
	});

	const openApprovalModal = useCallback((unitId: string) => {
		setIsApprovalModalOpen(true);
		setApprovalUnitId(unitId);
	}, []);

	const closeApprovalModal = useCallback(() => {
		setIsApprovalModalOpen(false);
		setApprovalUnitId(null);
	}, []);

	const openDeclineModal = useCallback((unitId: string) => {
		setIsDeclineModalOpen(true);
		setApprovalUnitId(unitId);
	}, []);

	const closeDeclineModal = useCallback(() => {
		setIsDeclineModalOpen(false);
		setApprovalUnitId(null);
	}, []);

	const openRevokeModal = useCallback((unitId: string) => {
		setIsRevokeModalOpen(true);
		setApprovalUnitId(unitId);
	}, []);

	const closeRevokeModal = useCallback(() => {
		setIsRevokeModalOpen(false);
		setApprovalUnitId(null);
	}, []);

	const columns = useMemo(() => {
		return getColumns({
			openNotesModal,
			isNetworkOperatorAssetEnquirer: !isAO,
			approve: openApprovalModal,
			decline: openDeclineModal,
			revoke: openRevokeModal,
			userId: user?.id,
			requestStatus,
		});
	}, [requestStatus, isAO, openApprovalModal, openDeclineModal, openRevokeModal, user?.id]);

	const onSearchHandler = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
	}, []);

	const search: Array<ITableSearch> = useMemo(() => {
		return [
			{
				initialValue: searchValue,
				name: 'searchValue',
				handler: onSearchHandler,
				placeholder: 'Search by user name',
			},
		];
	}, [onSearchHandler, searchValue]);

	const approveItemByUnit = async (comment: string) => {
		if (approvalUnitId) acceptAssetByApprovalUnit({ approvalUnitId, comment, itemId: asset.id });

		closeApprovalModal();
	};

	const declineItemByUnit = async (comment: string) => {
		if (approvalUnitId) declineAssetByApprovalUnit({ approvalUnitId, comment, itemId: asset.id });

		closeDeclineModal();
	};

	const revokeItemByUnit = async (comment: string) => {
		if (approvalUnitId) revokeAssetByApprovalUnit({ approvalUnitId, comment, itemId: asset.id });

		closeRevokeModal();
	};

	const toggleOnlyMyApprovals = useCallback(() => {
		setIsShowOnlyMyApprovals(prevState => !prevState);
	}, []);

	const filteredApprovalUnits = useMemo(() => {
		const filtered = approvalInfoItems.filter((unit) => {
			if (!isShowOnlyMyApprovals) return true;

			return unit.approvalUnit?.users?.some(item => item.id === user?.id);
		});

		if (!sortFieldName) return filtered.filter(item => !!item);

		return [...filtered].sort((a, b) => {
			const [first, second] = sortDirection ? [a, b] : [b, a];

			switch (sortFieldName) {
				case 'approvalUnit':
					return first.approvalUnit.name.localeCompare(second.approvalUnit.name);
				case 'createdAt':
					return first.approvalUnit?.updatedAt - second.approvalUnit?.updatedAt;
				default:
					return 0;
			}
		}).filter(item => !!item);
	}, [approvalInfoItems, isShowOnlyMyApprovals, user?.id, sortFieldName, sortDirection]);

	const withAssetOwnerApprovalUnit = !!user?.roles.some(({ name }) => name === EUserRoles.assetOwnerApprovalUnit);

	const isCustomFiltersShown = isAO && withAssetOwnerApprovalUnit;

	return (
		<div className={styles.approvalUnitsContainer}>
			<div className={styles.approvalUnitsInfo}>
				<div className={styles.assetInfo}>
					<div className={styles.assetInfoTitle}>
						<span>Asset ID&nbsp;</span>
						<span>{asset?.assetId}</span>
					</div>
					<ApprovalUnitsInfoRow
						info={asset.type.assetTypeName}
						isAlignCenter
						leftIcon={(
							<TextAvatar
								avatarId={asset.type.icon.id}
								fontSize='10px'
								isProfileLogo
								size='24px'
							/>
						)}
						title='Type'
					/>
					<ApprovalUnitsInfoRow
						info={asset?.assetAddress}
						title='Address'
					/>
					<ApprovalUnitsInfoRow
						info={`${formatDate(asset?.startRentDate)} - ${formatDate(asset?.endRentDate)}`}
						title='Dates'
					/>
				</div>
				<div className={styles.approvalUnitsCount}>
					<ApprovalUnitsInfoRow
						info={approvalInfoItems?.length}
						title='All'
					/>
					<ApprovalUnitsInfoRow
						info={asset?.pending}
						title='Pending'
					/>
					<ApprovalUnitsInfoRow
						info={asset?.approved}
						title='Approved'
					/>
					<ApprovalUnitsInfoRow
						info={asset?.disapproved}
						title='Disapproved'
					/>
				</div>
			</div>
			{isCustomFiltersShown && <div className={styles.customFilters}>
				<MultiSearch
					isOpenSearch={false}
					search={search}
				/>
				<div className={styles.sliderCheckboxContainer}>
					<SliderCheckbox
						checked={isShowOnlyMyApprovals}
						id='isShowOnlyMyApprovals'
						isLabelAfter
						label='Show only my Approvals'
						onChange={toggleOnlyMyApprovals}
					/>
				</div>
			</div>}
			<div className={styles.approvalUnitsTable}>
				<Table
					columns={columns}
					data={filteredApprovalUnits}
					isLoading={isLoadingApprovalInfo || isRequestItemsLoading}
					isOpenSearch={!!searchValue.length}
					isWithPagination={false}
					isWithoutCheckbox
					isWithoutDeleteAction
					isWithoutFilter
					searchThreshold={0}
					sort={sort}
				/>
			</div>
			{notesForModal && (
				<Modal
					isOpen={isNotesModalOpen}
					onClose={closeNotesModal}
					title='View note'
				>
					<NotesModal
						closeModal={closeNotesModal}
						note={notesForModal}
					/>
				</Modal>
			)}
			<Modal
				isOpen={isApprovalModalOpen}
				onClose={closeApprovalModal}
				title={confirmTitle}
			>
				<RequestActionConfirmationModal
					acceptButtonTitle={approveBtnTitle}
					closeModal={closeApprovalModal}
					isCommentRequired={false}
					onApprove={approveItemByUnit as TOnApprove}
				/>
			</Modal>
			<Modal
				isOpen={isDeclineModalOpen}
				onClose={closeDeclineModal}
				title={confirmTitle}
			>
				<RequestActionConfirmationModal
					acceptButtonTitle={disapproveBtnTitle}
					closeModal={closeDeclineModal}
					isCommentRequired={false}
					onApprove={declineItemByUnit as TOnApprove}
				/>
			</Modal>
			<Modal
				isOpen={isRevokeModalOpen}
				onClose={closeRevokeModal}
				title={confirmTitle}
			>
				<RequestActionConfirmationModal
					acceptButtonTitle={revokeBtnTitle}
					closeModal={closeRevokeModal}
					isCommentRequired={false}
					onApprove={revokeItemByUnit as TOnApprove}
				/>
			</Modal>
			{isLoadingApprovalInfo || isRequestItemsLoading && <SpinnerOverlay />}
		</div>
	);
};

export default memo(ApprovalUnitsModal);
